import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { Application, Paginated } from "../types/dtos";

export type ApplicationListParams = { page?: number; pageSize?: number; q?: string };

async function fetchApplications(params: ApplicationListParams): Promise<Paginated<Application>> {
  const res = await staffApi.get("/applications", { params });
  return res.data;
}

// A anotação explícita do retorno blinda quem consome o hook (as páginas) contra qualquer
// inferência incompleta do generic do react-query — sem ela, `data` podia cair para `any`.
export function useApplications(params: ApplicationListParams): UseQueryResult<Paginated<Application>> {
  return useQuery({
    queryKey: ["applications", params],
    queryFn: () => fetchApplications(params),
    placeholderData: (prev: Paginated<Application> | undefined) => prev,
  });
}

export type ApplicationFormInput = {
  name: string;
  slug: string;
  category?: string;
  description?: string;
  version?: string;
  contractObjectClause?: string;
};

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplicationFormInput) => staffApi.post("/applications", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<ApplicationFormInput> }) =>
      staffApi.patch(`/applications/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useSetApplicationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      staffApi.patch(`/applications/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useApplication(id: number | null): UseQueryResult<Application> {
  return useQuery({
    queryKey: ["applications", id],
    queryFn: async () => (await staffApi.get<Application>(`/applications/${id}`)).data,
    enabled: !!id,
  });
}

export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => staffApi.delete(`/applications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useAddPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      applicationId,
      input,
    }: {
      applicationId: number;
      input: { name: string; monthlyValue: number; billingCycle?: "MENSAL" | "ANUAL" };
    }) => staffApi.post(`/applications/${applicationId}/plans`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export type PlanUpdateInput = Partial<{
  name: string;
  monthlyValue: number;
  billingCycle: "MENSAL" | "ANUAL";
  active: boolean;
}>;

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, input }: { planId: number; input: PlanUpdateInput }) =>
      staffApi.patch(`/applications/plans/${planId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => staffApi.delete(`/applications/plans/${planId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}
